import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  OfficeAssignment,
  WorkflowOfficeConfig,
  WorkflowState,
  ActionType,
} from './office-assignment.model';
import { FormBuilder } from '@angular/forms';
import { WorkflowStateSelectorComponent } from '../../workflow-state-selector/workflow-state-selector.component';

@Component({
  selector: 'irembogov-office-assignment-config',
  templateUrl: './office-assignment-config.component.html',
  styleUrls: ['./office-assignment-config.component.scss'],
})
export class OfficeAssignmentConfigComponent implements OnInit, OnChanges {
  @Input() organizationId?: string;
  @Input() changeRequestJsonWorkflowData!: Record<string, unknown>;
  @Output() workflowUpdated = new EventEmitter<any>();
  @ViewChild('stateSelector') stateSelector!: WorkflowStateSelectorComponent;

  workflowConfig!: WorkflowOfficeConfig;
  assignments: OfficeAssignment[] = [];
  showForm = false;
  selectedState: WorkflowState | null = null;
  selectedAssignment: OfficeAssignment | null = null;

  constructor(private fb: FormBuilder) {}

  ngOnInit() {
    this.parseExistingAssignments();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['changeRequestJsonWorkflowData'] &&
      !changes['changeRequestJsonWorkflowData'].isFirstChange()
    ) {
      this.changeRequestJsonWorkflowData =
        changes['changeRequestJsonWorkflowData'].currentValue;

      // Re-parse the assignments to reflect the updated data
      this.parseExistingAssignments();
    }
  }

  parseExistingAssignments() {
    this.workflowConfig = {
      workflow: (this.changeRequestJsonWorkflowData['workflow'] as any[]) || [],
      nextSteps:
        (this.changeRequestJsonWorkflowData['nextSteps'] as any[]) || [],
      expectedProcessingTime:
        (this.changeRequestJsonWorkflowData[
          'expectedProcessingTime'
        ] as number) || 0,
      expectedProcessingMeasurementUnit:
        (this.changeRequestJsonWorkflowData[
          'expectedProcessingMeasurementUnit'
        ] as string) || '',
    };

    this.assignments = this.workflowConfig.workflow
      .map(step => this.extractOfficeAssignment(step))
      .filter(
        (assignment): assignment is OfficeAssignment => assignment !== null
      );
  }

  getWorkflowState(): WorkflowState {
    return (
      this.selectedState || {
        startState: '',
        event: '',
        endStateType: 'endStateOne',
        endStateCode: '',
        actionType: 'breakingAction',
      }
    );
  }

  getCurrentFormAssignment(): OfficeAssignment | null {
    if (this.selectedAssignment) {
      return this.selectedAssignment;
    }
    if (!this.selectedState) {
      return null;
    }
    return (
      this.assignments.find(
        a =>
          a.workflowContext.startState === this.selectedState?.startState &&
          a.workflowContext.event === this.selectedState?.event &&
          a.workflowContext.endStateType === this.selectedState?.endStateType
      ) || null
    );
  }

  onAddNew() {
    this.selectedAssignment = null;
    this.selectedState = null;
    this.showForm = true;
  }

  onEdit(assignment: OfficeAssignment) {
    this.selectedAssignment = assignment;
    this.selectedState = assignment.workflowContext;
    this.showForm = true;
  }

  onCancel() {
    this.showForm = false;
    this.selectedAssignment = null;
    this.selectedState = null;
  }

  onDelete(assignment: OfficeAssignment) {
    if (confirm('Are you sure you want to delete this office assignment?')) {
      // Find the corresponding step and end state
      const step = this.workflowConfig.workflow.find(
        s =>
          s.startState === assignment.workflowContext.startState &&
          s.event === assignment.workflowContext.event
      );

      if (step) {
        const endState = step[assignment.workflowContext.endStateType];
        // Remove office assignment action while preserving other actions
        this.removeOfficeAssignmentFromEndState(endState);

        // Update assignments list
        this.assignments = this.assignments.filter(
          a => !this.isSameAssignment(a, assignment)
        );
      }

      this.updateWorkflowConfigFromAssignments();
    }
  }

  private removeOfficeAssignmentFromEndState(endState: any) {
    if (endState.breakingAction?.actionType === 'OFFICE_ASSIGNMENT') {
      endState.breakingAction = null;
    }

    if (Array.isArray(endState.breakingActions)) {
      endState.breakingActions = endState.breakingActions.filter(
        (action: any) => action.actionType !== 'OFFICE_ASSIGNMENT'
      );
      if (endState.breakingActions.length === 0) {
        endState.breakingActions = null;
      }
    }

    if (Array.isArray(endState.nonBreakingActions)) {
      endState.nonBreakingActions = endState.nonBreakingActions.filter(
        (action: any) => action.actionType !== 'OFFICE_ASSIGNMENT'
      );
      if (endState.nonBreakingActions.length === 0) {
        endState.nonBreakingActions = null;
      }
    }
  }

  onStateSelected(state: WorkflowState) {
    if (state && state.startState && state.event && state.endStateType) {
      this.selectedState = state;
      const existingAssignment = this.getCurrentFormAssignment();
      if (existingAssignment) {
        this.selectedAssignment = existingAssignment;
      } else {
        this.selectedAssignment = null;
      }
    }
  }

  onAssignmentChange(assignment: OfficeAssignment) {
    if (this.selectedAssignment) {
      // If updating existing assignment
      const oldEndStateType =
        this.selectedAssignment.workflowContext.endStateType;
      const newEndStateType = assignment.workflowContext.endStateType;

      // Find the step that contains this assignment
      const step = this.workflowConfig.workflow.find(
        s =>
          s.startState === assignment.workflowContext.startState &&
          s.event === assignment.workflowContext.event
      );

      if (step) {
        // If moving between end states or changing action type
        if (
          oldEndStateType !== newEndStateType ||
          this.selectedAssignment.workflowContext.actionType !==
            assignment.workflowContext.actionType
        ) {
          // Remove from old location
          this.removeOfficeAssignmentFromEndState(step[oldEndStateType]);
        }
      }

      // Update assignments list
      this.assignments = this.assignments.map(a =>
        this.isSameAssignment(a, this.selectedAssignment!) ? assignment : a
      );
    } else {
      // New assignment
      this.assignments = [...this.assignments, assignment];
    }

    this.updateWorkflowConfigFromAssignments();
    this.showForm = false;
    this.selectedAssignment = null;
    this.selectedState = null;
  }

  private updateWorkflowConfigFromAssignments() {
    const updatedWorkflow = this.workflowConfig.workflow.map(step => {
      const matchingAssignment = this.assignments.find(
        a =>
          a.workflowContext.startState === step.startState &&
          a.workflowContext.event === step.event
      );

      if (matchingAssignment) {
        const endStateKey = matchingAssignment.workflowContext.endStateType;
        const updatedEndState = this.updateEndStateActions(
          step[endStateKey] || {},
          matchingAssignment
        );

        return {
          ...step,
          [endStateKey]: updatedEndState,
        };
      }
      return step;
    });

    this.workflowUpdated.emit(updatedWorkflow);
  }

  private updateEndStateActions(
    currentEndState: any,
    assignment: OfficeAssignment
  ) {
    // Create a deep copy to avoid modifying the original
    const updatedEndState = JSON.parse(JSON.stringify(currentEndState));

    // First, remove any existing office assignment from all possible locations
    this.removeOfficeAssignmentFromEndState(updatedEndState);

    // Create the new office assignment action
    const officeAssignmentAction = {
      actionType: 'OFFICE_ASSIGNMENT',
      args: this.createAssignmentArgs(assignment),
    };

    // Add the office assignment action to the appropriate location
    switch (assignment.workflowContext.actionType) {
      case 'breakingAction':
        updatedEndState.breakingAction = officeAssignmentAction;
        break;

      case 'breakingActions':
        if (!Array.isArray(updatedEndState.breakingActions)) {
          updatedEndState.breakingActions = [];
        }
        updatedEndState.breakingActions.push(officeAssignmentAction);
        break;

      case 'nonBreakingActions':
        if (!Array.isArray(updatedEndState.nonBreakingActions)) {
          updatedEndState.nonBreakingActions = [];
        }
        updatedEndState.nonBreakingActions.push(officeAssignmentAction);
        break;
    }

    return updatedEndState;
  }

  private createAssignmentArgs(assignment: OfficeAssignment): any {
    const baseArgs = {
      officeLevel: assignment.officeLevel,
      officeAssignmentType: this.getAssignmentTypeCode(assignment.type),
    };

    switch (assignment.type) {
      case 'FIXED':
        return {
          ...baseArgs,
          officeId: assignment.officeId,
        };
      case 'LOCATION_BASED':
        return {
          ...baseArgs,
          formFieldKey: assignment.formFieldKey,
        };
      case 'MULTIPLE_LOCATION':
        return {
          ...baseArgs,
          formFieldKeys: assignment.formFieldKeys,
        };
    }
  }

  private getAssignmentTypeCode(type: string): string {
    switch (type) {
      case 'FIXED':
        return 'CODE_FIXED_OFFICE';
      case 'LOCATION_BASED':
        return 'CODE_FROM_LOCATION';
      case 'MULTIPLE_LOCATION':
        return 'CODE_FROM_LOCATION';
      default:
        return 'CODE_FIXED_OFFICE';
    }
  }

  private isSameAssignment(
    a1: OfficeAssignment,
    a2: OfficeAssignment
  ): boolean {
    return (
      a1.workflowContext.startState === a2.workflowContext.startState &&
      a1.workflowContext.event === a2.workflowContext.event &&
      a1.workflowContext.endStateType === a2.workflowContext.endStateType
    );
  }

  private determineActionType(step: any, endStateKey: string): ActionType {
    const endState = step[endStateKey];
    const hasOfficeAssignment = this.findOfficeAssignmentAction(endState);

    if (!hasOfficeAssignment) return 'breakingAction'; // default value

    if (endState.breakingAction?.actionType === 'OFFICE_ASSIGNMENT') {
      return 'breakingAction';
    }
    if (
      endState.breakingActions?.some(
        (action: any) => action.actionType === 'OFFICE_ASSIGNMENT'
      )
    ) {
      return 'breakingActions';
    }
    if (
      endState.nonBreakingActions?.some(
        (action: any) => action.actionType === 'OFFICE_ASSIGNMENT'
      )
    ) {
      return 'nonBreakingActions';
    }

    return 'breakingAction';
  }

  extractOfficeAssignment(step: any): OfficeAssignment | null {
    // Check both end states for office assignment action
    const endStateOneAssignment = this.findOfficeAssignmentAction(
      step.endStateOne
    );
    const endStateTwoAssignment = this.findOfficeAssignmentAction(
      step.endStateTwo
    );

    if (!endStateOneAssignment && !endStateTwoAssignment) {
      return null;
    }

    const endStateType = endStateOneAssignment ? 'endStateOne' : 'endStateTwo';
    const endState = step[endStateType];
    const actionType = this.determineActionType(step, endStateType);
    const assignmentAction = this.findOfficeAssignmentAction(endState);

    if (!assignmentAction) {
      return null;
    }

    return {
      workflowContext: {
        startState: step.startState,
        event: step.event,
        endStateType,
        endStateCode: endState.stateCode,
        actionType,
        displayName: step.eventNames,
      },
      type: this.determineAssignmentType(assignmentAction.args),
      officeLevel: assignmentAction.args?.officeLevel,
      officeId: assignmentAction.args?.officeId,
      formFieldKey: assignmentAction.args.formFieldKey,
      formFieldKeys: assignmentAction.args.formFieldKeys,
    };
  }

  private findOfficeAssignmentAction(endState: any): any | null {
    if (!endState) return null;

    if (endState.breakingAction?.actionType === 'OFFICE_ASSIGNMENT') {
      return endState.breakingAction;
    }

    const breakingAction = endState.breakingActions?.find(
      (action: any) => action.actionType === 'OFFICE_ASSIGNMENT'
    );
    if (breakingAction) return breakingAction;

    const nonBreakingAction = endState.nonBreakingActions?.find(
      (action: any) => action.actionType === 'OFFICE_ASSIGNMENT'
    );
    if (nonBreakingAction) return nonBreakingAction;

    return null;
  }

  private determineAssignmentType(
    args: any
  ): 'FIXED' | 'LOCATION_BASED' | 'MULTIPLE_LOCATION' {
    if (args?.officeId) {
      return 'FIXED';
    }
    if (args?.formFieldKeys) {
      return 'MULTIPLE_LOCATION';
    }
    return 'LOCATION_BASED';
  }
}
